import { hashSha1 } from '@/utils/services/security';
import http from '../utils/services/http';
import { compressBase64Image } from '../utils/services/images';
import {
  solicitudMockData,
  createMockData,
  createAsistidoMockData,
  riesgoCrediticioMockData,
  getValidateMockData,
  getSucursalesMockData,
  getProvinciasMockData,
  getLocalidadesMockData,
  getTipoSociedadMockData,
  getTyCMockData,
  getPaqueteMockData,
  getDocumentacionTpoSociedadMockData,
  getCargosRepresentantesLegalesMockData,
  getVistaPreviaMockData,
} from './mockData';
import { useDocumentPropietariosStore, useDocumentsDeclaracionesStore, useDocumentStore, useImageStore, useStore } from '@/store/zustand-solicitud';
import { desCrypto } from '@/utils/services/cookies';
import { cortarArrayHastaUrl } from '@/utils/services/retomarSolicitud';
import dayjs from 'dayjs';
import { data } from '@/app/api/entidad/mockup';
import ErrorNotification from '@/components/ui/errorNotification';

const mockup = process.env.NEXT_PUBLIC_MOCKUP === 'true';
const mockupDelay = () => new Promise((resolve) => setTimeout(resolve, 1000));
const urlBASE = process.env.NEXT_PUBLIC_API_URL;
const withMockupDelay = async (accion, mockData = null) => {
  if (mockup) {
    await mockupDelay();
    return mockData !== null ? mockData : true;
  } else {
    return accion();
  }
};

/**
 * Saca el codigo de error de una respuesta fallida, sin asumir que siempre viene el envelope del
 * backend (AB#10815).
 *
 * El acceso directo a `error.data.errores[0].codigo` tiraba TypeError cuando el cuerpo no era ese
 * JSON: un 504 del gateway devuelve HTML. Esa excepcion salia de performRequest, la atrapaba el
 * catch del Checklist y terminaba en ERROR_INESPERADO_CHECKLIST, o sea la pantalla generica. El
 * codigo real y el estado HTTP se perdian justo cuando mas hacian falta.
 *
 * 502, 503 y 504 se mapean a SERVICIO_NO_DISPONIBLE, que es un mensaje que dice que reintentar
 * sirve. El resto conserva el comportamiento anterior.
 */
const leerCodigoDeError = (error) => {
  const codigoDelEnvelope = error?.data?.errores?.[0]?.codigo;
  if (codigoDelEnvelope) return codigoDelEnvelope;

  if (error?.codigo) return error.codigo;

  if ([502, 503, 504].includes(error?.status)) return 'SERVICIO_NO_DISPONIBLE';

  return 'UNKNOWN_ERROR';
};

const handleResponse = (response, errorRedirect = 'errors') => {
  if (!response.error) return response.data ?? true;
  window.location.replace(`${errorRedirect}?code=${response.codigo}`);
  return false;
};

const constants = {
  types: {
    fisica: 'fisica',
    credenciales: 'credenciales',
    asistida: 'asistido',
  },
  screens: {
    instructions: 'instructions',
    documento: 'documento',
    formManual: 'formManual',
    form: 'form',
    checklist: 'checklist',
    camera: 'camera',
    otp: 'otp',
    empty: 'empty',
    cameraBlocked: 'camera-blocked',
    rechazado: 'rechazado',
    selfie: 'selfie',
  },
  status: {
    pending: '1',
    approved: '2',
    rejected: '3',
    review: '4',
    pendingSignatures: '5',
    pendingLegalRepresentative: '6',
    pendingOfficialBusinessReview: '7',
    passedIndirect: '8',
    expired: '9',
  },
};

const performRequest = async (method, url, data = {}, mockData = null, headers = {}, opciones = {}) => {
  const accion = async () => {
    try {
      const config = {
        ...opciones,
        headers: {
          ...headers,
        },
      };

      const response = await http.request({
        method,
        url,
        data,
        ...config,
      });
      // const response = await http[method](url, data, config);
      return handleResponse(response);
    } catch (error) {
      const errorCode = leerCodigoDeError(error);

      if (errorCode === 'PDF417_NO_ENCONTRADO') {
        useStore.getState().setScreen(constants.screens.formManual);
        return false;
      }
      //Manejar el error y redirigir a error?code=
      window.location.replace(`errors?code=${errorCode}`);
      return false;
    }
  };
  return await withMockupDelay(accion, mockData);
};

const generateService =
  (method, endpoint, mockData, type, opciones = {}) =>
  async (data = {}) => {
    let solicitudRl = 'solicitudesRL';
    const id = sessionStorage.getItem('solicitud');
    const url = `${urlBASE}/${type ?? solicitudRl}/${id}${endpoint}`;
    return await performRequest(method, url, data, mockData, {}, opciones);
  };

const get = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudes/${id}`;
  return await performRequest('get', url, null, solicitudMockData);
};

const create = async (dorso, frente) => {
  frente = (await compressBase64Image(frente, 0.6)).split(',')[1];
  dorso = (await compressBase64Image(dorso, 0.6)).split(',')[1];
  const url = `${urlBASE}/solicitudesRL`;
  let solicitud = sessionStorage.getItem('solicitud');
  solicitud = solicitud === 'undefined' ? null : solicitud;

  const data = {
    entidadId: sessionStorage.getItem('entidadId'),
    frente,
    dorso,
    solicitudJuridicaId: sessionStorage.getItem('solicitudJ'),
    solicitudRepresentanteLegalId: solicitud,
    externalRefid: sessionStorage.getItem('ref_id'),
  };
  let response = await performRequest('post', url, data, createMockData);
  if (response) {
    let solicitudIdRl = response.solicitudRLId;
    sessionStorage.setItem('solicitud', solicitudIdRl);
    if (response.retomaSolicitud) {
      let steps = desCrypto(useStore.getState().steps);
      let ejecuteArray = await cortarArrayHastaUrl(steps, response.step);
      // AB#11507: siempre se pisa, para que /retomar no use un paso viejo de otra retoma.
      sessionStorage.setItem('retomar', response.step || '');
      if (ejecuteArray) {
        useStore.getState().setType('retoma');
        useStore.getState().setSteps(ejecuteArray);
        // window.location.replace(response.step)
      }
      window.location.replace('/retomar');
    }

    return response;
  }
};

const createAsistido = async (
  archivoAdjunto,
  nombre = null,
  apellido = null,
  genero = null,
  documento = null,
  documentotramite = null,
  codigoUsuario = null
) => {
  const url = `${urlBASE}/solicitudes/asistido`;
  const data = {
    sucursal: sessionStorage.getItem('sucursal'),
    entidadId: sessionStorage.getItem('entidadId'),
    archivoAdjunto,
    genero,
    documento,
    documentotramite,
    nombre,
    apellido,
    codigoUsuario,
  };
  return await performRequest('post', url, data, createAsistidoMockData);
};

const createJuridico = async (
  razonSocial = null,
  email = null,
  cuit = null,
  tipoSociedad = null,
  entidadId = sessionStorage.getItem('entidadId'),
  externalRefid = sessionStorage.getItem('ref_id')
) => {
  const url = `${urlBASE}/solicitudesJuridicas`;
  const cuitSinGuiones = cuit ? cuit.replace(/-/g, '') : null;

  const data = {
    razonSocial,
    email,
    cuit: cuitSinGuiones,
    tipoSociedad,
    entidadId,
    externalRefid,
  };
  let response = await performRequest('post', url, data);
  sessionStorage.setItem('solicitud', response.id);
  return response;
};

const updateFuenteIngreso = generateService('patch', '/fuente-ingreso', true);
const updateCuentaSueldo = generateService('patch', '/cuenta-sueldo', true);
const updateRenaper = generateService('patch', '/renaper', true);

const updateCuentaSueldoCustom = async (cuentaSueldo) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudes/${id}/cuenta-sueldo`;
  const data = { cuentaSueldo };
  const accion = async () => {
    const response = await http.patch(url, data);
    if (!response.error) {
      sessionStorage.setItem('solicitud', id);
      return true;
    }
    window.location.replace(`error?code=${response.codigo}`);
    return false;
  };
  return await withMockupDelay(accion, true);
};

const updateMorfologia = generateService('patch', '/addalia/morfologia', true);
const updateListaNegra = generateService('patch', '/lista-negra-interna-documento ', true);
const updateListaBlanca = generateService('patch', '/lista-blanca-interna', true);
const updatePruebaVida = generateService('patch', '/addalia/prueba-vida', true);
const updateEmailScoring = generateService('patch', '/contacto/scoring/email', true);
const sendEmailOtp = generateService('post', '/contacto/email/otp', true);

const updateEmail = async (email) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${id}/contacto/email`;
  const data = { email };
  return await performRequest('patch', url, data, true);
};

const validateEmailOtp = async (otp) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${id}/contacto/email/otp`;
  const data = { otp };
  return await performRequest('patch', url, data, true);
};

const updateTelefono = async (telefono) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${id}/contacto/telefono`;
  const data = { telefono };
  return await performRequest('patch', url, data, true);
};
const updateTelefonoScoring = generateService('patch', '/contacto/scoring/telefono', true);
const sendTelefonoOtp = generateService('post', '/contacto/telefono/otp', true);

const validateTelefonoOtp = async (otp) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${id}/contacto/telefono/otp`;
  const data = { otp };
  return await performRequest('patch', url, data, true);
};

const updateEmailScoringJuridico = generateService('patch', '/contacto/scoring/email', true, 'solicitudesJuridicas');
// AB#11073: en el flujo RL, despues de create() sessionStorage.solicitud = RL ID.
// El endpoint de OTP juridico necesita el ID de la SolicitudJuridica, que vive en
// solicitudJ. Se usa || (no ??) porque page.tsx guarda '' cuando no hay param 's'.
const sendEmailOtpJuridico = async () => {
  const id = sessionStorage.getItem('solicitudJ') || sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/contacto/email/otp`;
  return await performRequest('post', url, {}, true);
};

const updateEmailJuridico = async (email) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/contacto/email`;
  const data = { email };
  return await performRequest('patch', url, data, true);
};

const validateEmailOtpJuridico = async (otp) => {
  const id = sessionStorage.getItem('solicitudJ') || sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/contacto/email/otp`;
  const data = { otp };
  return await performRequest('patch', url, data, true);
};

const updateTelefonoJuridico = async (telefono) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudJuridica/${id}/contacto/telefono`;
  const data = { telefono };
  return await performRequest('patch', url, data, true);
};
const updateTelefonoScoringJuridico = generateService('patch', '/contacto/scoring/telefono', true, 'solicitudesJuridicas');
const sendTelefonoOtpJuridico = async () => {
  const id = sessionStorage.getItem('solicitudJ') || sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/contacto/telefono/otp`;
  return await performRequest('post', url, {}, true);
};

const validateTelefonoOtpJuridico = async (otp) => {
  const id = sessionStorage.getItem('solicitudJ') || sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/contacto/telefono/otp`;
  const data = { otp };
  return await performRequest('patch', url, data, true);
};

// AB#11481: una sola accion por canal (validate-telefono-otp / validate-email-otp) sirve
// para PJ y para PF/RL, asi que el endpoint lo decide el tipo de solicitud y no la accion.
// Es el mismo criterio que ya usaba el reenvio de otp.tsx. El envio (pagina) y la
// validacion (Checklist) pasan los dos por aca: si resolvieran distinto, el codigo se
// generaria en una solicitud y se validaria contra otra. 'retoma' solo se setea al crear
// una solicitud RL, asi que cae del lado RL, que es lo correcto.
const esSolicitudJuridica = () => useStore.getState().type === 'juridico';

const sendEmailOtpSegunTipo = () =>
  esSolicitudJuridica() ? sendEmailOtpJuridico() : sendEmailOtp();

const validateEmailOtpSegunTipo = (otp) =>
  esSolicitudJuridica() ? validateEmailOtpJuridico(otp) : validateEmailOtp(otp);

const sendTelefonoOtpSegunTipo = () =>
  esSolicitudJuridica() ? sendTelefonoOtpJuridico() : sendTelefonoOtp();

const validateTelefonoOtpSegunTipo = (otp) =>
  esSolicitudJuridica() ? validateTelefonoOtpJuridico(otp) : validateTelefonoOtp(otp);

const updatePadronA5 = generateService('patch', '/afip/a5', true);
const updatePadronA5Juridoc = generateService('patch', '/afip/a5', true, 'solicitudesJuridicas');
const updateSujetoObligado = generateService('patch', '/uif/sujeto-obligado', true);
const updateNosis = generateService('patch', '/nosis', true);
const updateNosisJuridico = generateService('patch', '/nosis', true, 'solicitudesJuridicas');
const updateWorldsys = generateService('patch', '/worldsys/search', true);
// const updateLegajoDigital = generateService('patch', '/legajo-digital', true);
const updateLegajoDigital = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `legajo-digital/tramite/solicitud-rl/${id}`;
  let response = await performRequest('patch', url);

  return response;
};
const updateAltaCuenta = generateService('patch', '/alta-cuenta', true);
const updateCuentaComitente = generateService('patch', '/alta-cuenta-comitente', true);
const updateAceptaTyc = generateService('patch', '/update-acepta-tyc', true);

const getValidateRl = async () => {
  const idSolicitud = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${idSolicitud}`;
  const headers = { includes: '2' };
  return await performRequest('get', url, null, getValidateMockData, headers);
};

const getValidateJuridico = async () => {
  const idSolicitud = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${idSolicitud}`;
  const headers = { includes: '2' };
  return await performRequest('get', url, null, getValidateMockData, headers);
};

// Vista previa y conformidad del representante legal (RF-ONB-19, AB#10741/US-7).
// Usan 'solicitudJ' y NO 'solicitud': en este punto del flujo la solicitud del representante
// todavia no existe, se crea recien al subir el DNI. El id que hay es el de la juridica, que
// llego por el parametro 's' del link de invitacion.
const getVistaPreviaJuridica = async () => {
  const idSolicitudJuridica = sessionStorage.getItem('solicitudJ');
  const url = `${urlBASE}/solicitudesJuridicas/${idSolicitudJuridica}/vista-previa-rl`;
  return await performRequest('get', url, null, getVistaPreviaMockData);
};

const updateConformidadRL = async (form) => {
  const idSolicitudJuridica = sessionStorage.getItem('solicitudJ');
  const url = `${urlBASE}/solicitudesJuridicas/${idSolicitudJuridica}/conformidad-rl`;
  const data = {
    solicitudRLId: form.solicitudRLId,
    aceptaTerminos: true,
    aceptaDeclaracionJurada: true,
  };
  return await performRequest('patch', url, data, true);
};

const getSucursales = async () => {
  const url = `${urlBASE}/sucursales`;
  return await performRequest('get', url, null, getSucursalesMockData);
};

const getProvincias = async () => {
  const url = `${urlBASE}/provincias`;
  return await performRequest('get', url, null, getProvinciasMockData);
};

const getLocalidades = async (idProvincia) => {
  const url = `${urlBASE}/provincias/${idProvincia}/localidades`;
  return await performRequest('get', url, null, getLocalidadesMockData);
};

const getTipoSociedad = async () => {
  const url = `${urlBASE}/tipoSociedades`;
  return await performRequest('get', url, null, getTipoSociedadMockData);
};

const getCargosRepresentantesLegales = async () => {
  const url = `${urlBASE}/cargosRespresentanteLegal`;
  return await performRequest('get', url, null, getCargosRepresentantesLegalesMockData);
};
const updateDomicilioComercial = async (form) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/domicilioComercial`;
  const data = form;
  return await performRequest('patch', url, data, null);
};

const updateDomicilioFiscal = async (form) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/domicilioFiscal`;
  const data = form;
  return await performRequest('patch', url, data, null);
};

const updateDatosAdicionales = async (nombre, apellido, dni, fecha, genero) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudes/${id}/persona-adicional`;
  const data = { nombre, apellido, dni, fecha, genero };
  return await performRequest('patch', url, data, true);
};

const updateDdjj = async (esFacta, esPEP, esOcde, esUif) => {
  const id = sessionStorage.getItem('solicitud');
  const data = { esFacta, esPEP, esOcde, esUif };
  const url = `${urlBASE}/solicitudes/${id}/ddjj`;
  return await performRequest('patch', url, data, true);
};

const updateDispositivo = async () => {
  const responseIp = await fetch('https://geolocation-db.com/json/');
  if (!responseIp) {
    return true;
  }
  const dataIp = await responseIp.json();
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${id}/dispositivo`;
  const data = {
    ip: dataIp?.IPv4 ?? '',
    dispositivo: window?.navigator?.userAgent ?? '',
  };
  return await performRequest('patch', url, data, true);
};

const updateCredenciales = async (usuario, password) => {
  password = await hashSha1(password);
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudes/${id}/credenciales`;
  const data = { usuario, password };
  return await performRequest('patch', url, data, true);
};

const update = async (form) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${id}`;
  const data = form;
  data.fechaNacimiento = dayjs(data.fechaNacimiento).format('DD/MM/YYYY');
  return await performRequest('put', url, data, true);
};
const updateJuridico = async (form) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}`;
  const data = form;
  return await performRequest('patch', url, data, true);
};

const updateMatrizRL = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${id}/matriz-riesgo`;
  const response = await performRequest('patch', url, {}, true);

  if (response) {
    const estado = response.estado;
    sessionStorage.setItem('status', estado);

    const statusRoutes = {
      [constants.status.pending]: '/status/rl/pendiente',
      [constants.status.approved]: '/status/rl/aprobada',
      [constants.status.rejected]: '/status/rl/rechazada',
      [constants.status.review]: '/status/rl/revisar',
      [constants.status.passedIndirect]: '/status/rl/aprobadoIndirectamente',
    };
    const redirectUrl = statusRoutes[estado.toString()];

    if (redirectUrl) {
      window.location.replace(redirectUrl);
      return false;
    }
  }
  return true;
};
const updateMatriz = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/matriz-riesgo`;
  let response = await performRequest('patch', url, {}, true);

  if (response) {
    const estado = response.estado;
    sessionStorage.setItem('status', estado);

    const statusRoutes = {
      [constants.status.pendingSignatures]: '/status/juridico/pendienteFirma',
      [constants.status.pendingLegalRepresentative]: '/status/juridico/pendienteRL',
      [constants.status.pendingOfficialBusinessReview]: '/status/juridico/pendienteRevision',
    };
    const redirectUrl = statusRoutes[estado.toString()];

    if (redirectUrl) {
      window.location.replace(redirectUrl);
      return false;
    }
  }
  return true;
};

const tycAsistido = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudes/${id}/tyc-asistido`;
  return await performRequest('patch', url, null, true);
};
const sendEmailsRl = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/representantes/email`;
  return await performRequest('patch', url, {}, true);
};

const aceptaTyc = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudes/${id}/tyc`;
  const response = await http.get(url);
  const aceptaTyC = response.data.aceptaTyC;

  if (!aceptaTyC) {
    window.location.replace(`/error?code=TYC_NO_ACEPTADO`);
    return false;
  }
  return true;
};

const emailRepresentaste = async (emails) => {
  const id = sessionStorage.getItem('solicitud');

  const url = `${urlBASE}/solicitudesJuridicas/${id}/representantes`;
  // const newEmails = emails.map((elemento) => elemento.email);
  const data = {
    representantesLegales: emails,
  };
  let response = await performRequest('post', url, data);

  return response;
};

const getDocumentacionTpoSociedad = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `/legajo-digital/solicitud-juridica/${id}/documentacion-tipo-sociedad?&obligatorio=true&opcional=true&alternativo=false`;
  let response = await performRequest('get', url, {}, getDocumentacionTpoSociedadMockData);
  response = response.filter((el) => el.nombre !== 'Contrato juridico');
  return response;
};

const updateDocumentacion = async () => {
  let documents = useDocumentStore.getState().documentosJuridico;
  const id = sessionStorage.getItem('solicitud');
  const url = `solicitudesJuridicas/${id}/documentacion`;
  
  // Filtrar solo los documentos que tienen archivo subido
  const documentsWithFiles = documents.filter(doc => doc.archivo && doc.nombreArchivo);
  
  const promises = documentsWithFiles.map((documento) => {
    let data = {
      archivo: documento.archivo,
      codMasterDoc: documento.codigo,
      nombreArchivo: documento.nombre + '.pdf',
      obligatoriedad: documento.obligatoriedad,
    };
    return performRequest('patch', url, data);
  });

  const responses = await Promise.all(promises);
  return responses;
};

const createTramiteLD = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `legajo-digital/tramite/solicitud-juridica/${id}`;
  let response = await performRequest('patch', url);

  return response;
};

const crearUrl = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/prueba-de-vida/${id}/crear-url`;
  const data = {
    userId: id,
  };

  return performRequest('patch', url, data);
};

// AB#11648: si el RL puede subir el contrato firmado ahora (PJ esperando firmas, plazo sin vencer,
// RL firmante y sin haber firmado). La pagina lo consulta antes de mostrar el formulario.
const getEstadoContrato = async (idRepresentanteLegal) => {
  const url = `${urlBASE}/solicitudesJuridicas/contratoRL/${idRepresentanteLegal}/estado`;
  return performRequest('get', url);
};

const updateContrato = async (value) => {
  const id = sessionStorage.getItem('solicitudJ');
  const url = `${urlBASE}/solicitudesJuridicas/contratoRL/${id}`;
  let response = await performRequest('post', url, value);
  if (response === '') {
    return true;
  }
  return response;
};

const updateJuridicoDdjj = async (form) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/ddjj`;
  let data = {
    ...form,
    constanciaSO: useDocumentsDeclaracionesStore.getState().documentosDeclaracionesJuridico,
  };
  let response = await performRequest('patch', url, data);
  return response;
};

const updateSelfie = async (form) => {
  let data = null;
  if (!form) {
    const raw = useImageStore.getState().selfieImage;
    data = raw?.startsWith('data:image') ? raw.split(',')[1] : raw;
  } else {
    data = form;
  }
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${id}/renaper-rostro`;
  let response = await performRequest('patch', url, { selfie: data });
  return response;
};

const getTyC = async () => {
  const id = sessionStorage.getItem('entidadId');
  const url = `${urlBASE}/entidades/${id}/terminos`;
  let response = await performRequest('get', url, {}, getTyCMockData);
  if (response === '') {
    return true;
  }
  return response;
};

const getPaquete = async () => {
  const url = `${urlBASE}/solicitudesJuridicas/producto`;
  let response = await performRequest('get', url, {}, getPaqueteMockData);
  if (response === '') {
    return true;
  }
  return response;
};

const updateRLDddj = async (form) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${id}/ddjj`;
  let data = {
    ...form,
    constanciaSO: useDocumentsDeclaracionesStore.getState().documentosDeclaracionesRL,
  };
  let response = await performRequest('patch', url, data);
  return response;
};

const getResult = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/prueba-de-vida/${id}/get-result`;

  let response = await performRequest('patch', url);

  return response;
};

const updateEstadoSimpleEnrollment = async (cancelarPrevio) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${process.env.NEXT_PUBLIC_API_URL}/solicitudesRL/${id}/enrollment-estado-simple`;
  const data = { cancelarPrevio };
  return await performRequest('patch', url, data);
};

// AB#11660 (RF-ONB-20): las consultas de estado de SocialNet hacen polling en el servidor hasta
// BindPagos.TiempoConsultaEstado (600 s por defecto). Con el timeout global de 60 s el navegador
// cortaba antes y el RL caia en la pantalla de error; el corte lo decide el backend.
const SIN_TIMEOUT_CLIENTE = { timeout: 0 };

const updateEstadoValidacion = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${process.env.NEXT_PUBLIC_API_URL}/solicitudesRL/${id}/consultar-estado-autenticacion`;
  return await performRequest('patch', url, {}, null, {}, SIN_TIMEOUT_CLIENTE);
};

const updateEstadoEnrollment = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${process.env.NEXT_PUBLIC_API_URL}/solicitudesRL/${id}/enrollment-estado`;
  return await performRequest('patch', url, {}, null, {}, SIN_TIMEOUT_CLIENTE);
};

const createEnrollment = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${process.env.NEXT_PUBLIC_API_URL}/solicitudesRL/${id}/enrollment-create`;
  return await performRequest('patch', url);
};

const createAutenticacion = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${process.env.NEXT_PUBLIC_API_URL}/solicitudesRL/${id}/crear-autenticacion`;
  return await performRequest('patch', url);
};

const updatePropietarios = async (propietarios) => {
  const id = sessionStorage.getItem('solicitud');
  let datos = useDocumentPropietariosStore.getState();

  const url = `${urlBASE}/solicitudesJuridicas/${id}/propietario`;
  const data = { propietarios, documentacionRespaldatoria: datos.documentoPropietarios };
  return await performRequest('patch', url, data, true);
};

const updateBeneficiarios = async (beneficiarios) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesJuridicas/${id}/beneficiario-final`;

  const path = useDocumentPropietariosStore.getState().pathDocumentosBeneficiario || [];

  if (!beneficiarios) {
    return await performRequest('patch', url, [], true);
  }

  const mapped = beneficiarios.map((b, i) => {
    // path[i] es el objeto de slots del beneficiario: { frente, dorso }, cada uno
    // { uid, archivoId }. Un slot ausente viaja como null y el backend lo interpreta como
    // "no cargado o quitado". Desde AB#10831 son dos caras mas el tipo declarado: el
    // backend elige con ese tipo el CodMasterDoc con el que cada cara llega al legajo.
    const documentos = path[i] || {};
    return {
      razonSocial: b.razonSocial,
      apellido: b.apellido,
      nombre: b.nombre,
      nroDocumento: b.nroDoc,
      tipoIdentificadorFiscal: b.tipoIdentificador,
      domicilioReal: b.domicilio,
      nacionalidad: b.nacionalidad,
      profesion: b.profesion,
      fechaNacimiento: b.fechaNacimiento,
      estadoCivil: b.estadoCivil,
      porcentajePropiedad: b.porcentaje,
      pep: b.pep === 'SI',
      tipoDocumentoAdjunto: b.tipoDocumentoAdjunto || null,
      documentoFrenteId: documentos.frente?.archivoId || null,
      documentoDorsoId: documentos.dorso?.archivoId || null,
    };
  });

  return await performRequest('patch', url, mapped, true);
  // return await Promise.all(mapped.map((beneficiario) => performRequest('patch', url, beneficiario, true)));
};

const updateAltaCuentaQr = generateService('patch', '/alta-cuenta-qr', true);
const updateCredencialesRL = generateService('patch', '/credenciales', true);
const updateAltaCuentaBind = generateService('patch', '/alta-cuenta-bind', true);
const updateExistePersona = generateService('patch', '/existe-persona', true);
const updateListaNegraBind = generateService('patch', '/lista-negra-bind', true);
const updateInformar = generateService('patch', '/informar', true);
const updateAltaRegistroEmail = generateService('patch', '/alta-registro-email', true);
const updateCrearAutenticacion = generateService('patch', '/crear-autenticacion', true);
const updateEnrollmentCreate = generateService('patch', '/enrollment-create', true);
const updateConsultarEstadoAutenticacion = generateService('patch', '/consultar-estado-autenticacion', true, undefined, SIN_TIMEOUT_CLIENTE);
const updateEnrollmentEstado = generateService('patch', '/enrollment-estado', true, undefined, SIN_TIMEOUT_CLIENTE);
const updateEnrollmentEstadoSimple = generateService('patch', '/enrollment-estado-simple', true);
const updateEnrollmentInfo = generateService('patch', '/enrollment-info-enrollment', true);
const updateAltaWallet = generateService('patch', '/alta-wallet', true);
const updateAsignarComercioWallet = generateService('patch', '/asignar-comercio-wallet', true);
const updateRefId = generateService('patch', '/update-refId', true);

const updateBlm = generateService('put', '/blm', true);
const updateWallet = generateService('put', '/wallet', true);

const getEstadoSolicitudRL = generateService('get', '/estado', {});
const getExternalRefId = generateService('get', '/externalRefid', {});
const getExternalRefIdById = async () => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudesRL/${id}/externalRefid`;
  return await performRequest('get', url);
};

// FaceTec (RF-ONB-18, AB#10705). El orquestador cuelga estas rutas de /solicitudes/{id}, no de
// /solicitudesRL. El requestBlob/responseBlob es opaco: el front lo pasa tal cual entre el Device SDK
// y el orquestador, sin leerlo ni guardarlo.
const getFaceTecSesionToken = generateService('get', '/biometria-facetec/sesion-token', {}, 'solicitudes');
const getFaceTecEstado = generateService('get', '/biometria-facetec/estado', {}, 'solicitudes');

// El header X-Testing-API-Header solo lo manda el SDK cuando la cuenta usa la Testing API (dev);
// en produccion no existe y el orquestador lo trata como opcional.
/**
 * @param {string} requestBlob
 * @param {string | null} [testingApiHeader]
 */
const postFaceTecVerificacion = async (requestBlob, testingApiHeader = null) => {
  const id = sessionStorage.getItem('solicitud');
  const url = `${urlBASE}/solicitudes/${id}/biometria-facetec/verificacion`;
  const headers = testingApiHeader ? { 'X-Testing-API-Header': testingApiHeader } : {};
  return await performRequest('post', url, { requestBlob }, {}, headers);
};

const updateSituacionBcraJuridico = generateService('patch', '/situacion-bcra', true, 'solicitudesJuridicas');

const updateAltaCuentaQrJuridico = generateService('patch', '/alta-cuenta-qr', true, 'solicitudesJuridicas');

const updateAltaWalletJuridico = generateService('patch', '/alta-wallet', true, 'solicitudesJuridicas');

const updateAsignarComercioWalletJuridico = generateService('patch', '/asignar-comercio-wallet', true, 'solicitudesJuridicas');

const confirmarBiometriaFaceTec = async () => {
  const estado = await getFaceTecEstado();
  if (estado && estado.exito === true) return true;
  // Sin verificacion exitosa no se avanza: vuelve al paso para que el RL reintente.
  window.location.replace('errors?code=PRUEBA_VIDA_INCOMPLETA');
  return false;
};

const runAction = async (actionId, idForm) => {
  let form = useStore.getState().form;
  let images = useImageStore.getState();
  form = form[idForm];
  switch (actionId) {
    case 'create':
      if (sessionStorage.getItem('manualFlowDone') === 'true') {
        sessionStorage.removeItem('manualFlowDone');
        return true;
      }
      return await create(images.backImage, images.frontImage);
    case 'create-juridico':
      return await createJuridico(form.razonSocial, form.email, form.cuit, form.tipoSociedad);
    case 'update-fuente-ingresos':
      return await updateFuenteIngreso(form.fuenteDeIngreso, form.ingreso);
    case 'update-cuenta-sueldo':
      return await updateCuentaSueldoCustom(form.cuentaSueldo);
    case 'update-riesgo-crediticio':
      return await riesgoCrediticio();
    case 'update-domicilio-entrega':
      return await updateDomicilioEntrega();
    case 'update-datos-adicionales':
      return await updateDatosAdicionales(form.nombre, form.apellido, form.dni, form.fecha, form.genero);
    case 'update-lista-negra':
      return await updateListaNegra();
    case 'update-lista-blanca':
      return await updateListaBlanca();
    case 'update-morfologia':
      return await updateMorfologia();
    case 'update-renaper':
      return await updateRenaper();
    case 'update-prueba-vida':
      return await updatePruebaVida(form.video);
    case 'update-email-scoring':
      return await updateEmailScoring();
    // AB#11481: el envio ya no es una accion. El Checklist corre despues de que el usuario
    // tipeo el codigo, asi que enviar aca generaba un codigo nuevo que invalidaba el que
    // estaba por validarse. Lo manda la pagina al tocar Siguiente cuando el paso tiene la
    // accion de verificar. Se quedan como no-op (y no se quitan del switch) para que un flujo
    // guardado con estas acciones no caiga en ACCION_NO_SOPORTADA.
    case 'send-email-otp':
    case 'send-email-otp-juridico':
    case 'send-telefono-otp':
    case 'send-telefono-otp-juridico':
      return true;
    // Accion unica por canal para PJ y PF/RL. Las *-juridico quedan como alias para los
    // flujos guardados antes de AB#11481.
    case 'validate-email-otp':
    case 'validate-email-otp-juridico':
      return await validateEmailOtpSegunTipo(form.otp);
    case 'validate-telefono-otp':
    case 'validate-telefono-otp-juridico':
      return await validateTelefonoOtpSegunTipo(form.otp);
    case 'update-telefono':
      return await updateTelefono(form.telefono);
    case 'update-telefono-scoring':
      return await updateTelefonoScoring();
    case 'update':
      return await update(form);
    case 'update-juridico':
      return await updateJuridico(form);
    case 'update-padron-a5':
      return await updatePadronA5();
    case 'update-padron-a5-juridico':
      return await updatePadronA5Juridoc();
    case 'update-sujeto-obligado':
      return await updateSujetoObligado();
    case 'update-nosis':
      return await updateNosis();
    case 'update-nosis-jr':
      return await updateNosisJuridico();
    case 'update-worldsys':
      return await updateWorldsys();
    case 'update-matriz':
      return await updateMatriz();
    case 'update-matriz-rl':
      return await updateMatrizRL();
    case 'update-legajo-digital-rl':
      return await updateLegajoDigital();
    case 'update-alta-cuenta':
      return await updateAltaCuenta();
    case 'update-credenciales':
      return await updateCredenciales(form.usuario, form.password);
    case 'update-dispositivo':
      return await updateDispositivo();
    case 'update-cuenta-comitente':
      return await updateCuentaComitente();
    case 'ddjj':
      return await updateDdjj(form.esFacta, form.esPEP, form.esOcde, form.esUif);
    case 'update-acepta-tyc':
      return await updateAceptaTyc();
    case 'update-tyc-asistido':
      return await tycAsistido();
    case 'acepta-tyc':
      return await aceptaTyc();
    case 'add-representantes':
      return await emailRepresentaste(form.emailsRL);
    case 'update-documentacion':
      return await updateDocumentacion(form.documentosJuridico);
    case 'create-tramite-ld':
      return await createTramiteLD();
    case 'update-ddjj':
      return await updateJuridicoDdjj(form);
    case 'update-renaper-rostro':
      return await updateSelfie();
    case 'update-ddjj-rl':
      return await updateRLDddj(form);
    case 'get-result':
      return await getResult();
    case 'send-emails-rl':
      return await sendEmailsRl();
    case 'update-estado-autenticacion':
      return await updateEstadoValidacion();
    case 'update-domiciliio-comercial':
      return await updateDomicilioComercial(form);
    case 'update-domiciliio-fiscal':
      return await updateDomicilioFiscal(form);
    case 'update-conformidad-rl':
      return await updateConformidadRL(form);
    case 'update-email-scoring-juridico':
      return await updateEmailScoringJuridico();
    case 'update-telefono-juridico':
      return await updateTelefonoJuridico(form.telefono);
    case 'update-telefono-scoring-juridico':
      return await updateTelefonoScoringJuridico();
    case 'update-alta-cuenta-comitente':
      return await updateCuentaComitente();
    case 'update-alta-cuenta-qr':
      return await updateAltaCuentaQr();
    case 'update-credenciales-rl':
      return await updateCredencialesRL(form.usuario, form.password);
    case 'update-alta-cuenta-bind':
      return await updateAltaCuentaBind();
    case 'update-existe-persona':
      return await updateExistePersona();
    case 'update-lista-negra-bind':
      return await updateListaNegraBind();
    case 'update-informar':
      return await updateInformar();
    case 'update-alta-registro-email':
      return await updateAltaRegistroEmail();
    case 'update-crear-autenticacion':
      return await updateCrearAutenticacion();
    case 'update-enrollment-create':
      return await updateEnrollmentCreate();
    case 'update-consultar-estado-autenticacion':
      return await updateConsultarEstadoAutenticacion();
    case 'update-enrollment-estado':
      return await updateEnrollmentEstado();
    case 'update-enrollment-estado-simple':
      return await updateEnrollmentEstadoSimple();
    case 'update-enrollment-info':
      return await updateEnrollmentInfo();
    case 'update-alta-wallet':
      return await updateAltaWallet();
    case 'update-asignar-comercio-wallet':
      return await updateAsignarComercioWallet();
    case 'update-refId':
      return await updateRefId();
    case 'update-blm':
      return await updateBlm();
    case 'update-wallet':
      return await updateWallet();
    case 'get-estado-solicitud-rl':
      return await getEstadoSolicitudRL();
    case 'get-external-refid':
      return await getExternalRefId();
    case 'get-external-refid-by-id':
      return await getExternalRefIdById();
    // AB#10705: la prueba de vida la hace la pagina /biometria con el Device SDK; esta accion solo
    // confirma con el orquestador que la ultima verificacion fue exitosa antes de avanzar.
    case 'biometria-facetec':
      return await confirmarBiometriaFaceTec();
    case 'update-situacion-bcra-juridico':
      return await updateSituacionBcraJuridico();
    case 'update-alta-cuenta-qr-juridico':
      return await updateAltaCuentaQrJuridico();
    case 'update-alta-wallet-juridico':
      return await updateAltaWalletJuridico();
    case 'update-asignar-comercio-wallet-juridico':
      return await updateAsignarComercioWalletJuridico();
    case 'update-propietarios':
      return await updatePropietarios(form.propietarios);
    case 'update-beneficiarios':
      return await updateBeneficiarios(form.beneficiarios);
    default:
      // Accion presente en la definicion del Flujo pero sin handler: es un
      // defecto de configuracion, no un `false` de negocio. Se lanza para que
      // el checklist lo capture y redirija al error page en vez de congelarse.
      throw new Error(`ACCION_NO_SOPORTADA:${actionId}`);
  }
};

const solicitud = {
  ...constants,
  get,
  create,
  createAsistido,
  updateFuenteIngreso,
  updateCuentaSueldo: updateCuentaSueldoCustom, // Se usa la función personalizada
  updateRenaper,
  updateMorfologia,
  updateListaNegra,
  updateListaBlanca,
  updatePruebaVida,
  getFaceTecSesionToken,
  postFaceTecVerificacion,
  getFaceTecEstado,
  updateEmail,
  updateEmailScoring,
  sendEmailOtp,
  validateEmailOtp,
  updateTelefono,
  updateTelefonoScoring,
  sendTelefonoOtp,
  validateTelefonoOtp,
  update,
  updatePadronA5,
  updateSujetoObligado,
  updateNosis,
  updateWorldsys,
  updateLegajoDigital,
  updateAltaCuenta,
  updateCuentaComitente,
  updateAceptaTyc,
  updateDdjj,
  updateDispositivo,
  updateCredenciales,
  updateSelfie,
  getValidateRl,
  getValidateJuridico,
  getSucursales,
  getProvincias,
  getLocalidades,
  runAction,
  tycAsistido,
  aceptaTyc,
  getTipoSociedad,
  updateDomicilioComercial,
  updateDomicilioFiscal,
  createJuridico,
  getDocumentacionTpoSociedad,
  crearUrl,
  updateContrato,
  getEstadoContrato,
  getTyC,
  getPaquete,
  updateEstadoSimpleEnrollment,
  updateEstadoValidacion,
  updateEstadoEnrollment,
  createEnrollment,
  createAutenticacion,
  getCargosRepresentantesLegales,
  getVistaPreviaJuridica,
  updateConformidadRL,
  updateEmailJuridico,
  sendEmailOtpJuridico,
  validateEmailOtpJuridico,
  sendTelefonoOtpJuridico,
  updateTelefonoJuridico,
  validateTelefonoOtpJuridico,
  sendEmailOtpSegunTipo,
  sendTelefonoOtpSegunTipo,
};

export default solicitud;
